import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DollarSign, Stethoscope, RefreshCw, CheckCircle, Clock, Download,
  CalendarIcon, TrendingUp, Users, FileText,
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

interface ProviderSummary {
  provider_id: string;
  name: string;
  email: string | null;
  completedConsultations: number;
  feePerConsultation: number;
  totalDue: number;
  totalPaid: number;
  pendingPayout: number;
}

interface ProviderPayout {
  id: string;
  provider_id: string;
  provider_name: string;
  consultation_count: number;
  fee_per_consultation: number;
  amount_cents: number;
  period_start: string;
  period_end: string;
  status: string;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DEFAULT_FEE_CENTS = 2000; // $20

const ProviderPayouts = () => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ProviderSummary[]>([]);
  const [payouts, setPayouts] = useState<ProviderPayout[]>([]);
  const [view, setView] = useState<"summary" | "payouts">("summary");
  const [generateOpen, setGenerateOpen] = useState<ProviderSummary | null>(null);
  const [genFee, setGenFee] = useState(String(DEFAULT_FEE_CENTS / 100));
  const [generating, setGenerating] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(startOfMonth(new Date()));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date());
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [rolesRes, profilesRes, appointmentsRes, payoutsRes, emailRes, checklistRes] = await Promise.all([
      supabase.from("user_roles").select("user_id").eq("role", "provider"),
      supabase.from("profiles").select("user_id, first_name, last_name"),
      supabase.from("appointments").select("id, provider_id, status, start_time, patient_id"),
      supabase.from("provider_payouts").select("*").order("created_at", { ascending: false }).limit(500),
      supabase.functions.invoke("list-user-emails"),
      supabase.from("session_checklist" as any).select("assessment_id, provider_id, finalized_at").not("finalized_at", "is", null),
    ]);

    const providerIds = new Set((rolesRes.data || []).map((r: any) => r.user_id));
    const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.user_id, p]));
    const emailMap: Record<string, string> = emailRes.data?.emails || {};

    // Build set of finalized sessions per provider
    const finalizedByProvider = new Map<string, number>();
    ((checklistRes.data as any[]) || []).forEach((c: any) => {
      if (providerIds.has(c.provider_id)) {
        finalizedByProvider.set(c.provider_id, (finalizedByProvider.get(c.provider_id) || 0) + 1);
      }
    });

    // Count completed consultations per provider (only finalized ones count)
    const consultCounts = new Map<string, number>();
    // We use finalized checklist counts instead of raw appointment counts
    finalizedByProvider.forEach((count, providerId) => {
      consultCounts.set(providerId, count);
    });

    // Aggregate payouts per provider
    const paidMap = new Map<string, number>();
    const pendingMap = new Map<string, number>();
    (payoutsRes.data || []).forEach((p: any) => {
      if (p.status === "paid") {
        paidMap.set(p.provider_id, (paidMap.get(p.provider_id) || 0) + p.amount_cents);
      } else {
        pendingMap.set(p.provider_id, (pendingMap.get(p.provider_id) || 0) + p.amount_cents);
      }
    });

    const summaries: ProviderSummary[] = Array.from(providerIds).map((id) => {
      const profile = profileMap.get(id);
      const name = profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || id.slice(0, 8) : id.slice(0, 8);
      const consults = consultCounts.get(id) || 0;
      return {
        provider_id: id,
        name,
        email: emailMap[id] || null,
        completedConsultations: consults,
        feePerConsultation: DEFAULT_FEE_CENTS,
        totalDue: consults * DEFAULT_FEE_CENTS,
        totalPaid: paidMap.get(id) || 0,
        pendingPayout: pendingMap.get(id) || 0,
      };
    }).sort((a, b) => b.completedConsultations - a.completedConsultations);

    setProviders(summaries);

    const payoutRows: ProviderPayout[] = (payoutsRes.data || []).map((p: any) => {
      const profile = profileMap.get(p.provider_id);
      return {
        ...p,
        provider_name: profile ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || p.provider_id.slice(0, 8) : p.provider_id.slice(0, 8),
      };
    });
    setPayouts(payoutRows);
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!generateOpen) return;
    setGenerating(true);
    const feeCents = Math.round((parseFloat(genFee) || 20) * 100);
    const consults = generateOpen.completedConsultations;

    if (consults <= 0) {
      toast({ title: "No consultations", description: "No completed consultations in this period.", variant: "destructive" });
      setGenerating(false);
      return;
    }

    const amountCents = consults * feeCents;
    const { error } = await supabase.from("provider_payouts").insert({
      provider_id: generateOpen.provider_id,
      consultation_count: consults,
      fee_per_consultation: feeCents,
      amount_cents: amountCents,
      period_start: format(dateFrom || startOfMonth(new Date()), "yyyy-MM-dd"),
      period_end: format(dateTo || new Date(), "yyyy-MM-dd"),
      status: "pending",
    });

    if (error) {
      toast({ title: "Failed to create payout", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Payout created", description: `${fmt(amountCents)} for ${consults} consultations.` });
      setGenerateOpen(null);
      fetchData();
    }
    setGenerating(false);
  };

  const markPaid = async (payoutId: string) => {
    const { error } = await supabase.from("provider_payouts").update({
      status: "paid",
      paid_at: new Date().toISOString(),
    }).eq("id", payoutId);
    if (error) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Payout marked as paid" });
      fetchData();
    }
  };

  const exportCSV = () => {
    const headers = ["Provider", "Email", "Consultations", "Fee/Consultation", "Total Due", "Paid", "Pending"];
    const rows = providers.map((p) => [
      p.name, p.email || "", p.completedConsultations, (p.feePerConsultation / 100).toFixed(2),
      (p.totalDue / 100).toFixed(2), (p.totalPaid / 100).toFixed(2), (p.pendingPayout / 100).toFixed(2),
    ].join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provider-payouts-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // KPI totals
  const totalConsultations = providers.reduce((s, p) => s + p.completedConsultations, 0);
  const totalDue = providers.reduce((s, p) => s + p.totalDue, 0);
  const totalPaid = providers.reduce((s, p) => s + p.totalPaid, 0);
  const totalPending = payouts.filter(p => p.status === "pending").reduce((s, p) => s + p.amount_cents, 0);

  // Chart data
  const chartData = providers.filter(p => p.completedConsultations > 0).slice(0, 8).map((p) => ({
    name: p.name.length > 12 ? p.name.slice(0, 12) + "…" : p.name,
    consultations: p.completedConsultations,
    due: p.totalDue / 100,
  }));

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant={view === "summary" ? "default" : "outline"} size="sm" onClick={() => setView("summary")}>
            Provider Summary
          </Button>
          <Button variant={view === "payouts" ? "default" : "outline"} size="sm" onClick={() => setView("payouts")}>
            Payout History
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1", !dateFrom && "text-muted-foreground")}>
                <CalendarIcon size={12} />
                {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1", !dateTo && "text-muted-foreground")}>
                <CalendarIcon size={12} />
                {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={fetchData}><RefreshCw size={14} /></Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Consultations", value: totalConsultations, icon: FileText, color: "text-primary" },
          { label: "Total Due", value: fmt(totalDue), icon: DollarSign, color: "text-foreground" },
          { label: "Paid Out", value: fmt(totalPaid), icon: CheckCircle, color: "text-primary" },
          { label: "Pending Payouts", value: fmt(totalPending), icon: Clock, color: "text-champagne" },
        ].map((k) => (
          <Card key={k.label} className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{k.label}</span>
                <k.icon size={16} className={k.color} />
              </div>
              <p className="font-display text-2xl text-foreground">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {view === "summary" ? (
        <>
          {/* Chart */}
          {chartData.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-display text-lg flex items-center gap-2">
                  <TrendingUp size={16} /> Consultations by Provider
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Bar dataKey="consultations" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Consultations" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Provider Table */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Stethoscope size={16} /> Provider Consultation Payouts
              </CardTitle>
              <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1" disabled={providers.length === 0}>
                <Download size={14} /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {providers.length === 0 ? (
                <p className="text-center text-muted-foreground py-12 text-sm">No providers found</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Provider</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Consultations</TableHead>
                        <TableHead>Fee/Consult</TableHead>
                        <TableHead>Total Due</TableHead>
                        <TableHead>Paid</TableHead>
                        <TableHead>Pending</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {providers.map((p) => (
                        <TableRow key={p.provider_id}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{p.email || "—"}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{p.completedConsultations}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{fmt(p.feePerConsultation)}</TableCell>
                          <TableCell className="font-medium">{fmt(p.totalDue)}</TableCell>
                          <TableCell className="text-primary font-medium">{fmt(p.totalPaid)}</TableCell>
                          <TableCell>
                            {p.pendingPayout > 0 ? (
                              <Badge variant="secondary" className="text-xs text-champagne">{fmt(p.pendingPayout)}</Badge>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs gap-1"
                              onClick={() => { setGenerateOpen(p); setGenFee(String(p.feePerConsultation / 100)); }}
                              disabled={p.completedConsultations === 0}
                            >
                              <DollarSign size={12} /> Generate Payout
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        /* Payout History */
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-display text-lg">Provider Payout History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {payouts.length === 0 ? (
              <p className="text-center text-muted-foreground py-12 text-sm">No payouts yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Consultations</TableHead>
                      <TableHead>Fee/Consult</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Paid At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payouts.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">{p.provider_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(p.period_start), "MMM d")} – {format(new Date(p.period_end), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{p.consultation_count}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{fmt(p.fee_per_consultation)}</TableCell>
                        <TableCell className="font-medium">{fmt(p.amount_cents)}</TableCell>
                        <TableCell>
                          <Badge variant={p.status === "paid" ? "default" : "secondary"} className="text-xs capitalize">
                            {p.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {p.paid_at ? format(new Date(p.paid_at), "MMM d, yyyy") : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {p.status === "pending" && (
                            <Button variant="outline" size="sm" onClick={() => markPaid(p.id)} className="gap-1 text-xs">
                              <CheckCircle size={12} /> Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Generate Payout Dialog */}
      <Dialog open={!!generateOpen} onOpenChange={(open) => !open && setGenerateOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Generate Payout — {generateOpen?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Period</Label>
                <p className="text-sm font-medium">
                  {dateFrom ? format(dateFrom, "MMM d") : "—"} – {dateTo ? format(dateTo, "MMM d, yyyy") : "now"}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Completed Consultations</Label>
                <p className="text-sm font-medium">{generateOpen?.completedConsultations || 0}</p>
              </div>
            </div>
            <div>
              <Label>Fee per Consultation ($)</Label>
              <Input type="number" min={0} step={1} value={genFee} onChange={(e) => setGenFee(e.target.value)} />
            </div>
            <div className="bg-secondary/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Payout Amount</p>
              <p className="font-display text-xl text-primary">
                {fmt((generateOpen?.completedConsultations || 0) * Math.round((parseFloat(genFee) || 0) * 100))}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {generateOpen?.completedConsultations || 0} consultations × ${parseFloat(genFee) || 0} each
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(null)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generating}>
              {generating ? "Creating…" : "Create Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderPayouts;
